import { useState } from 'react';
import { Plus, Target, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { GoalForm } from '@/components/goals/GoalForm';
import { GoalCard } from '@/components/goals/GoalCard';
import { DataImportDialog } from '@/components/import/DataImportDialog';
import { useGoals, FinancialGoal, CreateGoalInput } from '@/hooks/useGoals';
import { useToast } from '@/hooks/use-toast';
import { PremiumPageGate } from '@/components/subscription/PremiumPageGate';

export default function Goals() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<FinancialGoal | null>(null);
  const { toast } = useToast();

  const { goals, isLoading, createGoal, updateGoal, deleteGoal, bulkCreateGoals } = useGoals();

  const handleImportGoals = async (data: Record<string, unknown>[]) => {
    await bulkCreateGoals.mutateAsync(data as unknown as CreateGoalInput[]);
    toast({
      title: 'Importación exitosa',
      description: `Se importaron ${data.length} metas correctamente`,
    });
  };

  const handleCreate = (data: CreateGoalInput) => {
    createGoal.mutate(data);
  };

  const handleEdit = (goal: FinancialGoal) => {
    setEditingGoal(goal);
    setFormOpen(true);
  };

  const handleUpdate = (data: CreateGoalInput) => {
    if (editingGoal) {
      updateGoal.mutate({ id: editingGoal.id, ...data });
      setEditingGoal(null);
    }
  };

  const handleDelete = (id: string) => {
    deleteGoal.mutate(id);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingGoal(null);
  };

  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => g.status === 'completed');
  const pausedGoals = goals.filter(g => g.status === 'paused');

  return (
    <PremiumPageGate
      feature="goals"
      title="Metas financieras"
      description="Define objetivos de ahorro e inversión con Premium."
    >
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Metas Financieras</h1>
            <p className="text-muted-foreground">
              Define y da seguimiento a tus objetivos de ahorro e inversión
            </p>
          </div>

          <div className="flex gap-2">
            <DataImportDialog
              type="goals"
              onImport={handleImportGoals}
              trigger={
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </Button>
              }
            />
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva meta
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : goals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">Sin metas financieras</h3>
            <p className="text-muted-foreground mb-4">
              Crea tu primera meta para comenzar a dar seguimiento a tus objetivos
            </p>
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Crear primera meta
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active goals */}
            {activeGoals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-success" />
                  Metas activas ({activeGoals.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeGoals.map((goal) => (
                    <GoalCard
                      key={goal.id}
                      goal={goal}
                      onEdit={() => handleEdit(goal)}
                      onDelete={() => handleDelete(goal.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Paused goals */}
            {pausedGoals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-warning" />
                  Metas pausadas ({pausedGoals.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pausedGoals.map((goal) => (
                    <GoalCard
                      key={goal.id}
                      goal={goal}
                      onEdit={() => handleEdit(goal)}
                      onDelete={() => handleDelete(goal.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Completed goals */}
            {completedGoals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground" />
                  Metas completadas ({completedGoals.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {completedGoals.map((goal) => (
                    <GoalCard
                      key={goal.id}
                      goal={goal}
                      onEdit={() => handleEdit(goal)}
                      onDelete={() => handleDelete(goal.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Goal form modal */}
        <GoalForm
          open={formOpen}
          onOpenChange={handleCloseForm}
          onSubmit={editingGoal ? handleUpdate : handleCreate}
          initialData={editingGoal || undefined}
          isEditing={!!editingGoal}
        />
      </div>
    </AppLayout>
    </PremiumPageGate>
  );
}
